import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useModuleAccess from '../../hooks/useModuleAccess';
import { FULL_VERSION } from '../../config/version';
import { CALENDAR_LABEL, EVENTS_NAV_LABEL } from '../events/eventLabels';
// Sidebar hover fix 2026-10-25: the sub-menus use the SAME timing as the Communications status-icon menu and the action
// pills (Val: "same cadence as the message status icons"). One source of truth, so they can never drift apart.
import { cascadeDelayMs, MENU_CLOSE_DWELL_MS, MENU_ITEM_RISE_EASING, MENU_ITEM_RISE_MS, MENU_OPEN_DWELL_MS } from '../../lib/uiMotion';
import { claimOpenMenu } from '../../lib/menuCoordinator';
import { hasFineHover, useDesktopNav } from './navLayout';

// BL-177 Step 2f: calmer sidebar motion. Opening and closing animate over 300 ms (ease-in-out, no snap); hovering the sidebar opens it
// only after the pointer has RESTED on it for 250 ms, and it closes 400 ms after the pointer leaves. With "reduce motion" on, nothing animates.
// Sidebar hover fix: the two hover numbers now come from the shared menu timing (they were already the same values).
export const SIDEBAR_ANIMATION_MS = 300;
export const SIDEBAR_HOVER_OPEN_MS = MENU_OPEN_DWELL_MS;
export const SIDEBAR_HOVER_CLOSE_MS = 400;
function prefersReducedMotion() {
    try {
        return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        return false;
    }
}

// UX pass 2026-10-25: the item pieces live OUTSIDE LeftSidebar so they are not rebuilt (and their permission checks re-run)
// every time the menu opens or closes. `ui` carries the few things they need from the sidebar.
function itemClass(ui, { indent = false, active = false, isRevealed = true, flyout = false } = {}) {
    const compact = ui.collapsed && !flyout;
    return `w-full min-h-[44px] flex items-center gap-3 text-left ${flyout ? 'py-2' : 'py-3'} ${
        indent ? 'transition-all duration-300' : ''
    } ${
        compact ? 'justify-center px-0' : indent ? 'px-4 pl-12' : 'px-4'
    } ${
        indent && !isRevealed ? 'opacity-0 translate-x-2' : 'opacity-100 translate-x-0'
    } ${
        active
            ? 'bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)] border-l-4 border-[var(--nav-active-edge)]'
            : ui.theme === 'dark'
                ? 'text-gray-300 hover:bg-gray-700 focus-visible:bg-gray-700'
                : 'text-gray-700 hover:bg-gray-50 focus-visible:bg-gray-100'
    }`;
}

// A flown-out sub-menu tells its items how to behave (labels always shown) and how to close the whole chain after a choice.
const FlyoutContext = React.createContext(null);

// Each flown-out option fades in and rises a little, one after another (the status-icon menu's cascade).
function cascadeStyle(flyoutItem) {
    if (!flyoutItem) return undefined;
    const delay = flyoutItem.revealed ? cascadeDelayMs(flyoutItem.index, flyoutItem.count) : 0;
    return {
        transitionProperty: 'opacity, transform',
        transitionDuration: `${MENU_ITEM_RISE_MS}ms`,
        transitionTimingFunction: MENU_ITEM_RISE_EASING,
        transitionDelay: `${delay}ms`,
        opacity: flyoutItem.revealed ? 1 : 0,
        transform: flyoutItem.revealed ? 'translateY(0)' : 'translateY(4px)',
    };
}

function NavItem({ ui, icon, label, active, onClick, requiredAccess = null, indent = false, delay = 0, isRevealed = true, flyoutItem = null }) {
    const flyout = React.useContext(FlyoutContext);
    const { allowed, loading } = useModuleAccess(requiredAccess?.module, requiredAccess?.action, requiredAccess?.entityId);
    if (requiredAccess && (loading || !allowed)) return null;
    const inFlyout = Boolean(flyoutItem);
    const showLabel = inFlyout || !ui.collapsed;
    return (
        <button
            type="button"
            onClick={() => { onClick?.(); if (inFlyout) flyout?.closeAll(); if (!ui.desktop) ui.onMobileClose?.(); }}
            aria-label={showLabel ? undefined : label}
            aria-current={active ? 'page' : undefined}
            title={showLabel ? undefined : label}
            data-nav-item=""
            style={inFlyout ? cascadeStyle(flyoutItem) : indent ? { transitionDelay: `${delay}ms` } : {}}
            className={itemClass(ui, { indent: indent && !inFlyout, active, isRevealed, flyout: inFlyout })}
        >
            <span className={`text-xl ${showLabel ? '' : 'flex items-center justify-center w-full'}`}>{icon}</span>
            {showLabel && <span className="font-medium whitespace-nowrap">{label}</span>}
        </button>
    );
}

const FOCUSABLE_ITEMS = 'button:not([disabled])';
// The panel's own options only (a nested sub-menu sits inside the panel's markup but has its own keyboard handling).
function ownItems(panel) {
    return Array.from(panel?.querySelectorAll(FOCUSABLE_ITEMS) || []).filter((button) => button.closest('[data-nav-flyout]') === panel);
}

// Sidebar hover fix 2026-10-25 (Val: "nor do the submenus cascade (same cadence as the message status icons)").
// A menu item with children. Two ways it shows them:
//   - FLY-OUT (desktop rail or desktop full menu): resting the pointer on it for 250 ms opens a panel just to the RIGHT of the
//     menu, level with the item, so it never covers the item it came from; it closes 400 ms after the pointer has left both
//     the item and the panel. Click, tap, Enter, Space or the right arrow opens it too; up/down arrows move inside it; left
//     arrow or Escape closes it and puts focus back on the item. Only one sub-menu (or any other hover menu) is open at a time.
//   - IN PLACE (the phone drawer, and the section of the page you're on while the full menu is showing): the children sit
//     indented under the item, and a tap/click folds them away or back, exactly as before. That keeps "where am I" visible.
function NavGroup({ ui, icon, label, children, groupKey, active = false, requiredAccess = null, flyoutItem = null, indent = false }) {
    const parentFlyout = React.useContext(FlyoutContext);
    const inFlyout = Boolean(flyoutItem);
    const { allowed, loading } = useModuleAccess(requiredAccess?.module, requiredAccess?.action, requiredAccess?.entityId);
    const menuId = `ia-nav-${groupKey}-${useId().replace(/:/g, '')}`;
    const inline = !ui.desktop || (!inFlyout && !ui.collapsed && active);
    const [inlineOpen, setInlineOpen] = useState(active);
    const [flyOpen, setFlyOpen] = useState(false);
    const [revealed, setRevealed] = useState(false);
    const [position, setPosition] = useState(null);
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const openTimerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const focusFirstRef = useRef(false);
    useEffect(() => { if (active) setInlineOpen(true); }, [active]);

    const clear = (ref) => { if (ref.current) { clearTimeout(ref.current); ref.current = null; } };
    const closeNow = useCallback(() => {
        clear(openTimerRef);
        clear(closeTimerRef);
        setFlyOpen(false);
    }, []);
    // Switching to the in-place style (or losing the desktop layout) closes any open fly-out.
    useEffect(() => { if (inline) closeNow(); }, [inline, closeNow]);
    useEffect(() => () => { clear(openTimerRef); clear(closeTimerRef); }, []);

    // One hover menu open at a time, app-wide (menuCoordinator) - a nested sub-menu keeps its parent open.
    useLayoutEffect(() => {
        if (!flyOpen) return undefined;
        return claimOpenMenu(menuId, { parentId: parentFlyout?.menuId ?? null, close: closeNow });
    }, [flyOpen, menuId, parentFlyout, closeNow]);

    // Options cascade in on the frame after the panel appears (same as the status-icon menu).
    useEffect(() => {
        if (!flyOpen) { setRevealed(false); return undefined; }
        const frame = requestAnimationFrame(() => setRevealed(true));
        return () => cancelAnimationFrame(frame);
    }, [flyOpen]);

    // Where the panel goes: flush against the right edge of the menu (or of the parent sub-menu), level with this item.
    // It follows the menu while the menu's width animates open or shut.
    const anchorElement = useCallback(() => (inFlyout ? parentFlyout?.panelRef.current : ui.drawerRef?.current) || wrapperRef.current, [inFlyout, parentFlyout, ui.drawerRef]);
    useLayoutEffect(() => {
        if (!flyOpen) { setPosition(null); return undefined; }
        const place = () => {
            const anchor = anchorElement();
            const button = buttonRef.current;
            if (!anchor || !button) return;
            const edge = anchor.getBoundingClientRect();
            const row = button.getBoundingClientRect();
            const height = panelRef.current?.offsetHeight || 0;
            const maxTop = Math.max(8, window.innerHeight - height - 8);
            setPosition({ left: Math.round(edge.right), top: Math.round(Math.min(Math.max(row.top, 8), maxTop)) });
        };
        place();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null;
        const anchor = anchorElement();
        if (observer && anchor) observer.observe(anchor);
        // The width transition doesn't always report every frame; follow it for its whole length too.
        let frame = 0;
        const until = Date.now() + SIDEBAR_ANIMATION_MS + 100;
        const follow = () => { place(); if (Date.now() < until) frame = requestAnimationFrame(follow); };
        frame = requestAnimationFrame(follow);
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            observer?.disconnect();
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [flyOpen, anchorElement, ui.collapsed]);

    // Keyboard opens move focus to the first option once it's there.
    useEffect(() => {
        if (!flyOpen || !focusFirstRef.current) return;
        focusFirstRef.current = false;
        ownItems(panelRef.current)[0]?.focus();
    }, [flyOpen]);

    const insideMenu = useCallback((node) => Boolean(node && (
        wrapperRef.current?.contains(node)
        || node.closest?.(`[data-nav-chain~="${menuId}"]`)
    )), [menuId]);

    // A tap or click anywhere else closes an open fly-out (touch and keyboard opens have no pointer-leave to close them).
    useEffect(() => {
        if (!flyOpen) return undefined;
        const outside = (event) => { if (!insideMenu(event.target)) closeNow(); };
        document.addEventListener('mousedown', outside);
        document.addEventListener('touchstart', outside);
        return () => {
            document.removeEventListener('mousedown', outside);
            document.removeEventListener('touchstart', outside);
        };
    }, [flyOpen, insideMenu, closeNow]);

    if (requiredAccess && (loading || !allowed)) return null;

    const hoverable = () => !inline && ui.desktop && ui.canHover();
    const handleMouseEnter = () => {
        if (!hoverable()) return;
        clear(closeTimerRef);
        if (flyOpen || openTimerRef.current) return;
        openTimerRef.current = setTimeout(() => { openTimerRef.current = null; setFlyOpen(true); }, MENU_OPEN_DWELL_MS);
    };
    const handleMouseLeave = () => {
        if (!hoverable()) return;
        clear(openTimerRef);
        if (!flyOpen || closeTimerRef.current) return;
        closeTimerRef.current = setTimeout(() => { closeTimerRef.current = null; setFlyOpen(false); }, MENU_CLOSE_DWELL_MS);
    };
    const handleBlur = (event) => {
        if (inline || !flyOpen) return;
        if (event.relatedTarget && insideMenu(event.relatedTarget)) return;
        if (!event.relatedTarget) return; // the window lost focus; leave it as it is
        closeNow();
    };

    const openFromKeyboard = () => {
        clear(openTimerRef);
        clear(closeTimerRef);
        if (flyOpen) { ownItems(panelRef.current)[0]?.focus(); return; }
        focusFirstRef.current = true;
        setFlyOpen(true);
    };
    const handleClick = (event) => {
        if (inline) { setInlineOpen((value) => !value); return; }
        clear(openTimerRef);
        clear(closeTimerRef);
        // event.detail === 0 means Enter or Space, not a pointer: open and step into the panel.
        if (event.detail === 0) { openFromKeyboard(); return; }
        setFlyOpen((value) => !value);
    };
    const handleButtonKeyDown = (event) => {
        if (inline) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); event.stopPropagation(); openFromKeyboard(); }
        else if (event.key === 'Escape' && flyOpen) { event.preventDefault(); event.stopPropagation(); closeNow(); }
    };
    const handlePanelKeyDown = (event) => {
        const items = ownItems(panelRef.current);
        const index = items.indexOf(document.activeElement);
        const move = (next) => { event.preventDefault(); event.stopPropagation(); items[(next + items.length) % items.length]?.focus(); };
        if (event.key === 'ArrowDown') move(index + 1);
        else if (event.key === 'ArrowUp') move(index < 0 ? items.length - 1 : index - 1);
        else if (event.key === 'Home') move(0);
        else if (event.key === 'End') move(items.length - 1);
        else if (event.key === 'ArrowLeft' || event.key === 'Escape') {
            if (!panelRef.current?.contains(document.activeElement) || document.activeElement?.closest('[data-nav-flyout]') !== panelRef.current) return;
            event.preventDefault();
            event.stopPropagation();
            closeNow();
            buttonRef.current?.focus();
        }
    };

    const showLabel = inFlyout || !ui.collapsed;
    const expanded = inline ? inlineOpen : flyOpen;
    const childUi = { ...ui, collapsed: false };
    const kids = React.Children.toArray(children).filter(React.isValidElement);
    const chain = [parentFlyout?.chain, menuId].filter(Boolean).join(' ');
    const flyoutValue = {
        menuId,
        chain,
        panelRef,
        closeAll: () => { closeNow(); parentFlyout?.closeAll(); },
    };
    const panelId = `${menuId}-panel`;
    return (
        <div
            ref={wrapperRef}
            className="relative"
            data-nav-group={groupKey}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onBlur={handleBlur}
        >
            <button
                ref={buttonRef}
                type="button"
                onClick={handleClick}
                onKeyDown={handleButtonKeyDown}
                aria-expanded={expanded}
                aria-controls={expanded ? panelId : undefined}
                aria-haspopup={inline ? undefined : 'true'}
                aria-label={showLabel ? undefined : label}
                title={showLabel ? undefined : label}
                data-nav-item=""
                style={inFlyout ? cascadeStyle(flyoutItem) : undefined}
                className={`${itemClass(ui, { active: active && !(inline && inlineOpen), flyout: inFlyout, indent: indent && !inFlyout })} justify-between`}
            >
                <div className={`flex items-center gap-3 ${showLabel ? '' : 'w-full justify-center'}`}>
                    <span className={`text-xl ${showLabel ? '' : 'flex items-center justify-center'}`}>{icon}</span>
                    {showLabel && <span className="font-medium whitespace-nowrap">{label}</span>}
                </div>
                {showLabel && (inline
                    ? <span aria-hidden="true" className={`transition-transform duration-300 ${inlineOpen ? 'rotate-180' : ''} ${ui.theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>▼</span>
                    : <span aria-hidden="true" className={`ml-2 ${ui.theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>›</span>)}
            </button>
            {inline && inlineOpen && (
                <div id={panelId} role="group" aria-label={`${label} menu`}>
                    {kids.map((child) => React.cloneElement(child, { ui: childUi, indent: true, isRevealed: true }))}
                </div>
            )}
            {!inline && flyOpen && (
                <FlyoutContext.Provider value={flyoutValue}>
                    {/* position: fixed lets the panel escape the menu's own scroll/clip box; it sits to the right of the menu. */}
                    <div
                        ref={panelRef}
                        id={panelId}
                        role="group"
                        aria-label={`${label} menu`}
                        data-nav-flyout=""
                        data-nav-chain={chain}
                        onKeyDown={handlePanelKeyDown}
                        style={{ position: 'fixed', left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' }}
                        className={`z-[60] min-w-[13rem] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-r-lg border py-1 shadow-2xl ${
                            ui.theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                        }`}
                    >
                        <div aria-hidden="true" className={`px-4 pt-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide ${ui.theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{label}</div>
                        {kids.map((child, index) => React.cloneElement(child, {
                            ui: childUi,
                            indent: false,
                            flyoutItem: { index, count: kids.length, revealed },
                        }))}
                    </div>
                </FlyoutContext.Provider>
            )}
        </div>
    );
}

// UX pass (Val: "Calendar becomes a parent item with a sub-menu: Events first and Calendar below it"). Only for a company
// with the Events pack on; otherwise Calendar stays one item. The parent shows if the person may open either child.
function CalendarGroup({ ui, currentView, navigate, isUser }) {
    const events = useModuleAccess(isUser ? 'events' : undefined, isUser ? 'view' : undefined);
    const dispatch = useModuleAccess('dispatch', 'view');
    const showEvents = isUser ? (!events.loading && events.allowed) : true;
    const showCalendar = !dispatch.loading && dispatch.allowed;
    const inside = currentView === 'events' || currentView === 'schedule-calendar';
    if (!showEvents && !showCalendar) return null;
    return (
        <NavGroup ui={ui} icon="🗓️" label={CALENDAR_LABEL} groupKey="calendar" active={inside}>
            {showEvents && <NavItem ui={ui} icon="🎉" label={EVENTS_NAV_LABEL} active={currentView === 'events'} onClick={() => navigate('/events')} />}
            {showCalendar && <NavItem ui={ui} icon="📅" label={CALENDAR_LABEL} active={currentView === 'schedule-calendar'} onClick={() => navigate('/schedule-calendar')} />}
        </NavGroup>
    );
}

function LeftSidebar({ currentView, financialSubView, collapsed: desktopCollapsed, onToggleCollapse, mobileOpen = false, onMobileToggle, onMobileClose, user, onLogout, theme = 'light', bookingsLabel = 'Engagements', sidebarMode = 'hover', tenantName = '', isPlatformSuperAdmin = false, isOrgAdmin = false, isUser = false, eventsEnabled = false }) {
    // Keep desktop preference independent of the temporary phone/tablet overlay.
    // Sidebar hover fix: "desktop" now also covers a 768-1023px window with a mouse or trackpad (see navLayout.js) - before,
    // those windows got the phone drawer, which has no rail to hover, so the menu could never open on hover.
    const handleLayoutChange = useCallback(() => { onMobileClose?.(); }, [onMobileClose]);
    const desktop = useDesktopNav(handleLayoutChange);
    const drawerRef = useRef(null);
    const flyover = sidebarMode !== 'pinned';
    const collapsedTarget = desktop ? desktopCollapsed : !mobileOpen;
    const toggleMenu = desktop ? onToggleCollapse : onMobileToggle;
    useEffect(() => {
        if (desktop || !mobileOpen) return undefined;
        const previous = document.activeElement;
        const drawer = drawerRef.current;
        const focusable = () => Array.from(drawer.querySelectorAll('button, [href], select, [tabindex="0"]')).filter((el) => el.getClientRects().length);
        drawer.querySelector('button')?.focus();
        const onKey = (event) => {
            if (event.key === 'Escape') { event.preventDefault(); onMobileClose?.(); }
            if (event.key === 'Tab') {
                const items = focusable();
                const first = items[0]; const last = items[items.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('keydown', onKey); previous?.focus(); };
    }, [desktop, mobileOpen, onMobileClose]);
    const navigate = useNavigate();
    const location = useLocation();
    const openTimerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const collapsedRef = useRef(collapsedTarget);
    const toggleRef = useRef(toggleMenu);
    collapsedRef.current = collapsedTarget;
    toggleRef.current = toggleMenu;
    // The layout (labels, padding) follows the animation: it expands at once and collapses only after the width has finished shrinking,
    // so the labels never vanish before the bar has closed.
    const [collapsed, setLayoutCollapsed] = useState(collapsedTarget);
    useEffect(() => {
        if (!collapsedTarget) {
            setLayoutCollapsed(false);
            return undefined;
        }
        if (prefersReducedMotion()) {
            setLayoutCollapsed(true);
            return undefined;
        }
        const timer = setTimeout(() => setLayoutCollapsed(true), SIDEBAR_ANIMATION_MS);
        return () => clearTimeout(timer);
    }, [collapsedTarget]);

    const clearTimer = (ref) => {
        if (ref.current) {
            clearTimeout(ref.current);
            ref.current = null;
        }
    };

    useEffect(() => {
        clearTimer(openTimerRef);
        clearTimer(closeTimerRef);
    }, [desktop, sidebarMode]);

    // Hover and keyboard reveal belong to "Expand on hover" on a desktop layout with a mouse. "Pinned open" never opens or closes by itself.
    const canHover = () => desktop && hasFineHover();
    const autoReveal = () => desktop && flyover;
    // Pointer arrives: a hidden sidebar opens only after the pointer has rested here for SIDEBAR_HOVER_OPEN_MS (a pointer just passing
    // the edge opens nothing); an open one just cancels any pending close.
    const handleMouseEnter = () => {
        if (!autoReveal() || !canHover()) return;
        clearTimer(closeTimerRef);
        if (collapsedRef.current && !openTimerRef.current) {
            openTimerRef.current = setTimeout(() => {
                openTimerRef.current = null;
                if (collapsedRef.current) toggleRef.current();
            }, SIDEBAR_HOVER_OPEN_MS);
        }
    };

    // Pointer leaves: cancel a pending open, and close SIDEBAR_HOVER_CLOSE_MS later (coming back in time cancels that).
    // A flown-out sub-menu counts as part of the sidebar (React treats it as inside), so moving onto it keeps the menu open.
    const handleMouseLeave = () => {
        if (!autoReveal() || !canHover()) return;
        clearTimer(openTimerRef);
        clearTimer(closeTimerRef);
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            if (!collapsedRef.current) toggleRef.current();
        }, SIDEBAR_HOVER_CLOSE_MS);
    };

    // Keyboard: tabbing into the rail flies the menu out at once; tabbing out of it (or Escape) tucks it away again.
    // A mouse click also moves focus; only keyboard focus opens it here (the pointer has its own rest-to-open rule above).
    const pointerFocusRef = useRef(false);
    const handlePointerDown = () => {
        pointerFocusRef.current = true;
        setTimeout(() => { pointerFocusRef.current = false; }, 0);
    };
    const handleFocus = () => {
        if (!autoReveal() || pointerFocusRef.current) return;
        clearTimer(closeTimerRef);
        if (collapsedRef.current) toggleRef.current();
    };
    const handleBlur = (event) => {
        if (!autoReveal() || !event.relatedTarget) return;
        if (drawerRef.current?.contains(event.relatedTarget)) return;
        if (!collapsedRef.current) toggleRef.current();
    };
    const handleKeyDown = (event) => {
        if (!autoReveal() || event.key !== 'Escape' || collapsedRef.current) return;
        event.preventDefault();
        toggleRef.current();
    };

    useEffect(() => () => {
        clearTimer(openTimerRef);
        clearTimer(closeTimerRef);
    }, []);

    const ui = { collapsed, desktop, theme, onMobileClose, canHover, drawerRef };
    // Desktop placement is decided here (not by a CSS breakpoint) so it matches the layout choice above at every width.
    // No transform on the desktop menu: a transform would trap the position: fixed sub-menus inside the menu's clip box.
    const placement = desktop
        ? (flyover
            ? `absolute inset-y-0 left-0 z-40 ${collapsedTarget ? '' : 'shadow-2xl'}`
            : 'static z-auto')
        : `fixed inset-y-0 left-0 z-50 ${mobileOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'}`;
    const touchTarget = 'min-w-[44px] min-h-[44px]';
    return (
        <>
            {!desktop && mobileOpen && <button type="button" aria-label="Close navigation backdrop" onClick={onMobileClose} className="fixed inset-0 z-40 bg-black/50" />}
            {/* "Expand on hover": the slim rail keeps its 48px place in the page; the open menu flies out over the page. */}
            {flyover && desktop && <div className="block w-12 shrink-0" aria-hidden="true" data-sidebar-spacer />}
            <div
                id="app-navigation"
                ref={drawerRef}
                role={!desktop && mobileOpen ? 'dialog' : undefined}
                aria-modal={!desktop && mobileOpen ? true : undefined}
                aria-label="Navigation menu"
                data-sidebar-state={collapsedTarget ? 'collapsed' : 'open'}
                data-sidebar-mode={flyover ? 'hover' : 'pinned'}
                data-sidebar-layout={desktop ? 'desktop' : 'drawer'}
                className={`${placement} shrink-0 border-r flex flex-col overflow-hidden transition-[width,transform] duration-300 ease-in-out motion-reduce:transition-none ${
                    theme === 'dark'
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-white border-gray-200'
                } ${collapsedTarget ? 'w-12' : 'w-64'}`}
                style={!desktop ? { paddingTop: 'env(safe-area-inset-top)', paddingLeft: 'env(safe-area-inset-left)', paddingBottom: 'env(safe-area-inset-bottom)' } : undefined}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseDown={handlePointerDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            >
                <div className={`border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} ${collapsed ? 'py-3' : 'p-6'}`}>
                    <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between gap-2'}`}>
                        <button
                            type="button"
                            onClick={toggleMenu}
                            aria-label={collapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
                            aria-expanded={!collapsed}
                            className={`${touchTarget} ${collapsed ? 'w-full flex items-center justify-center' : ''} text-2xl hover:text-[var(--accent-ink)] ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                            title={collapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
                        >
                            ☰
                        </button>
                        {!collapsed && (
                            <div className="flex-1 min-w-0">
                                <h2 className="truncate text-xl font-bold text-[var(--ink)]">{tenantName || 'IntelAgent'}</h2>
                                <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Powered by smarTEKnowledgy</p>
                                <p className={`mt-0.5 text-[10px] text-[var(--ink-muted)]`}>{FULL_VERSION}</p>
                            </div>
                        )}
                    </div>
                </div>
                <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4">
                    <NavItem ui={ui} icon="📊" label="Dashboard" active={currentView === 'dashboard'} onClick={() => navigate('/dashboard')} />
                    <NavItem ui={ui} icon="👥" label="CRM" requiredAccess={isUser ? { module: 'crm', action: 'view' } : null} active={currentView === 'clients'} onClick={() => navigate('/clients')} />
                    <NavItem ui={ui} icon="🧭" label={bookingsLabel} requiredAccess={isUser ? { module: 'dispatch', action: 'view' } : null} active={currentView === 'engagements'} onClick={() => navigate('/engagements')} />
                    {eventsEnabled
                        ? <CalendarGroup ui={ui} currentView={currentView} navigate={navigate} isUser={isUser} />
                        : <NavItem ui={ui} icon="🗓️" label={CALENDAR_LABEL} requiredAccess={{ module: 'dispatch', action: 'view' }} active={currentView === 'schedule-calendar'} onClick={() => navigate('/schedule-calendar')} />}
                    {/* UX pass: one Dispatcher item. Affiliates is a section inside it (same /dispatch/affiliates address, same permission). */}
                    <NavItem ui={ui} icon="🚐" label="Dispatcher" requiredAccess={{ module: 'dispatch', action: 'view' }} active={currentView === 'dispatch' || location.pathname.startsWith('/dispatch')} onClick={() => navigate('/dispatch')} />
                    <NavItem ui={ui} icon="💬" label="Communications" requiredAccess={isUser ? { module: 'comms', action: 'view' } : null} active={currentView === 'communications'} onClick={() => navigate('/communications')} />
                    <NavItem ui={ui} icon="✅" label="Tasks" requiredAccess={isUser ? { module: 'tasks', action: 'view' } : null} active={currentView === 'tasks'} onClick={() => navigate('/tasks')} />
                    <NavGroup ui={ui} icon="💰" label="Financials" groupKey="financials" requiredAccess={{ module: 'financial', action: 'view' }} active={currentView === 'financials'}>
                        <NavGroup ui={ui} icon="💳" label="Sales" groupKey="financials-sales"
                            active={currentView === 'financials' && ['invoicing', 'auto-billing', 'payments', 'scheduled-payments'].includes(financialSubView)}>
                            <NavItem ui={ui} icon="📄" label="Invoicing"
                                requiredAccess={{ module: 'financial', action: 'view' }}
                                active={currentView === 'financials' && financialSubView === 'invoicing'}
                                onClick={() => navigate('/financials/invoicing')} />
                            <NavItem ui={ui} icon="🔄" label="Auto-Billing"
                                requiredAccess={{ module: 'financial', action: 'view' }}
                                active={currentView === 'financials' && financialSubView === 'auto-billing'}
                                onClick={() => navigate('/financials/auto-billing')} />
                            <NavItem ui={ui} icon="💵" label="Payments"
                                requiredAccess={{ module: 'financial', action: 'view' }}
                                active={currentView === 'financials' && financialSubView === 'payments'}
                                onClick={() => navigate('/financials/payments')} />
                            <NavItem ui={ui} icon="📆" label="Scheduled Payments"
                                requiredAccess={{ module: 'financial', action: 'view' }}
                                active={currentView === 'financials' && financialSubView === 'scheduled-payments'}
                                onClick={() => navigate('/financials/scheduled-payments')} />
                        </NavGroup>
                        <NavGroup ui={ui} icon="🏦" label="Banking" groupKey="financials-banking"
                            active={currentView === 'financials' && ['transactions', 'chart-of-accounts'].includes(financialSubView)}>
                            <NavItem ui={ui} icon="📊" label="Transactions"
                                requiredAccess={{ module: 'financial', action: 'view' }}
                                active={currentView === 'financials' && financialSubView === 'transactions'}
                                onClick={() => navigate('/financials/transactions')} />
                            <NavItem ui={ui} icon="📖" label="Chart of Accounts"
                                requiredAccess={{ module: 'financial', action: 'view' }}
                                active={currentView === 'financials' && financialSubView === 'chart-of-accounts'}
                                onClick={() => navigate('/financials/chart-of-accounts')} />
                        </NavGroup>
                    </NavGroup>

                    <NavItem ui={ui} icon="⚙️" label="Settings" requiredAccess={isUser ? { module: 'settings', action: 'view' } : null} active={currentView === 'settings'} onClick={() => navigate('/settings')} />
                    {(isOrgAdmin || isPlatformSuperAdmin) && (
                        <NavItem ui={ui} icon="🏢" label="Admin Center" active={currentView === 'admin'} onClick={() => navigate('/admin')} />
                    )}
                    {isPlatformSuperAdmin && (
                        <NavItem ui={ui} icon="🛠️" label="Support Console" active={currentView === 'support-console'} onClick={() => navigate('/support-console')} />
                    )}
                    {isPlatformSuperAdmin && (
                        <NavItem ui={ui} icon="🛡️" label="Platform Roles" active={currentView === 'platform-roles'} onClick={() => navigate('/platform-roles')} />
                    )}
                </nav>
                {!collapsed && (
                    <div className={`p-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            <div className="font-medium truncate">{user.email}</div>
                            <button type="button" onClick={onLogout} className={`${touchTarget} mt-2 text-[var(--accent-ink)] hover:text-[var(--accent-ink-hover)]`}>
                                Logout
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default LeftSidebar;
